import { SpinnerLoader } from "@/components/ui/Loader.tsx";
import {
  useIniPayments,
  useMe,
  useNotifications,
  useUserProducts,
} from "@/data/user";
import { INTERNAL_DATE_FORMAT } from "@/utils/agenda";
import useEventNotifications from "@/utils/hooks/useEventNotifications";
import { filterNotificationsUnseen } from "@/utils/notifications";
import { uuid } from "@/utils/string";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import NotificationItem from "./NotificationItem";
import StaticBookExam from "./StaticBookExam";
import StaticNotificationItem from "./StaticNotificationItem";

dayjs.extend(relativeTime);
const CHECKOUT_BASE = import.meta.env.EDS_CHECKOUT_BASE;

const iniPayments = [4990, 7990, 11990, 14990];

const payNowMessage = (
  <Trans
    i18nKey="home.finalize_payment"
    components={{
      b: <span />,
      span: <span className="font-semibold"></span>,
    }}
  />
);

const Notifications = () => {
  const { me } = useMe();
  const { t } = useTranslation();
  const { payments } = useIniPayments();
  const eventsNotifications = useEventNotifications();
  const { notifications, isLoading } = useNotifications();
  const { isLoading: isLoadingProducts } = useUserProducts();

  const [modified, setModified] = useState([]);
  const [showNotifyForPayRest, setShowNotifyForPayRest] =
    useState<boolean>(false);

  const newNotifications = useMemo(
    () => filterNotificationsUnseen(modified || []),
    [modified],
  );

  const handleComplete = () => {
    const token = payments?.item?.payment_data?.token;
    const productToken = payments?.item?.payment_data?.product_token;
    const url = `${CHECKOUT_BASE}existing-user/${productToken}/${token}`;
    const newWindow = window.open(url, "_blank");
    if (newWindow) newWindow.opener = null;
  };

  useEffect(() => {
    const mod = notifications?.items?.slice();

    if (eventsNotifications) {
      mod?.push(...(eventsNotifications as never[]));
    }

    mod?.sort((a, b) =>
      dayjs(a?.created_at, INTERNAL_DATE_FORMAT).diff(
        dayjs(b?.created_at, INTERNAL_DATE_FORMAT),
      ),
    );

    setModified(mod as never);
  }, [notifications, eventsNotifications]);

  useEffect(() => {
    if (payments?.item?.paid_total) return;

    const paymentAmount = (
      (payments?.item?.payment_data?.amount || 0) * 100
    ).toFixed(0);

    setShowNotifyForPayRest(iniPayments.includes(parseInt(paymentAmount)));
  }, [isLoading, isLoadingProducts, payments]);

  return (
    <div className="xl:flex-[1_1_30%] flex-[1_1_100%] gap-2 p-4 rounded-md flex flex-col h-full bg-[#ffffff66]">
      {isLoading ? (
        <div className="flex items-center justify-center w-full h-full">
          <SpinnerLoader />
        </div>
      ) : (
        <>
          <p className="hidden mb-2 text-sm font-semibold text-white lg:block">
            <Trans i18nKey="home.course.tab_two" default="What's up next" />
          </p>

          {showNotifyForPayRest && (
            <StaticNotificationItem
              text={payNowMessage}
              color="text-white"
              buttonText={t("btns.pay_now")}
              background="bg-brand-secondary"
              buttonColor="text-[--secondary]"
              handleComplete={handleComplete}
              buttonBackground="bg-white"
            />
          )}

          {!["es", "de"].includes(me?.item?.user?.lang ?? "") && (
            <StaticBookExam />
          )}

          {newNotifications?.length ? (
            <div className="gap-2 flex-[1_1_0] flex flex-col overflow-y-auto w-full h-full min-h-0">
              {newNotifications.map((item) => (
                <NotificationItem key={uuid()} item={item} />
              ))}
            </div>
          ) : (
            payments?.item.paid_total && (
              <div className="flex items-center justify-center w-full h-full">
                <span className="text-sm text-white">
                  {t("home.no_new_messages")}
                </span>
              </div>
            )
          )}
        </>
      )}
    </div>
  );
};

export default Notifications;
